import styled, { css } from 'styled-components';

interface StyledModalProps {
    modalLargerThanOverlay?: boolean;
}

export const StyledModal = styled.div<StyledModalProps>`
    display: flex;
    justify-content: center;
    overflow: auto;
    position: fixed;
    z-index: 1051;
    inset: 0;

    ${({ modalLargerThanOverlay }) =>
        !modalLargerThanOverlay &&
        css`
            align-items: center;
        `}
    padding: 34px 16px;

    .helix-modal__gradient {
        position: absolute;
        left: 0;
        right: 16px; // Prevents overlap with scrollbar
        bottom: 0;
        height: 84px;
        pointer-events: none;
        background: linear-gradient(to top, rgba(255, 255, 255, 1) 5%, rgba(255, 255, 255, 0) 100%);
        z-index: 2;
    }
`;

export const StyledOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);

    @keyframes overlayAnimateIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @keyframes overlayAnimateOut {
        from {
            opacity: 1;
        }
        to {
            opacity: 0;
        }
    }

    &.animate-in,
    &.animate-out {
        animation-duration: 0.45s;
        animation-fill-mode: forwards;
    }

    &.animate-in {
        animation-name: overlayAnimateIn;
    }

    &.animate-out {
        animation-name: overlayAnimateOut;
    }
`;

interface StyledModalContentProps {
    closing?: boolean;
    animate?: boolean;
    size?: 'lg' | 'default';
    hasScrollbar?: boolean; // New prop to indicate if scrollbar is present
}

export const StyledModalContent = styled.div<StyledModalContentProps>`
    background: ${({ theme }) => theme.modal?.backgroundColor};
    border-radius: 8px;
    box-shadow: ${({ theme }) => theme.modal?.boxShadow};
    display: flex;
    flex-flow: column;
    max-width: 500px;
    height: fit-content;
    position: relative;
    z-index: 1;
    max-height: 100%;
    ${({ size }) =>
        size === 'lg' &&
        css`
            width: 800px;
            max-width: 100%;
        `}

    // on small screen sizes set the max width to 100% to make sure the modal is always full width
    @media (max-width: ${({ theme }) => theme.breakPoints?.small}) {
        max-width: 100%;
        width: 100%;
    }

    @keyframes modalContentAnimateIn {
        from {
            opacity: 0;
            margin-top: -100px;
        }
        to {
            opacity: 1;
            margin-top: 0px;
        }
    }

    @keyframes modalContentAnimateOut {
        from {
            opacity: 1;
            margin-top: 0px;
        }
        to {
            opacity: 0;
            margin-top: -100px;
        }
    }

    &.animate-in,
    &.animate-out {
        animation-duration: 0.45s;
        animation-fill-mode: forwards;
    }

    &.animate-in {
        animation-name: modalContentAnimateIn;
    }

    &.animate-out {
        animation-name: modalContentAnimateOut;
    }
`;

export const StyledModalHeader = styled.div`
    display: flex;
    flex: 1;
    padding: 32px 34px 16px 34px;
    flex-grow: 0;
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 0;
    }

    .helix-modal__header-content {
        flex: 1 1;
    }
`;

export const StyledModalBody = styled.div`
    flex: 1;
    padding: 16px 34px 24px 34px;
    overflow: auto;
`;

export const StyledModalFooter = styled.div`
    flex: 1;
    padding: 24px 34px;
    display: flex;
    flex-grow: 0;
    .helix-modal__footer-buttons {
        display: flex;
        flex: 1;
        justify-content: flex-end;
        button + button {
            margin-left: 12px;
        }
    }
`;
